import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getUserPublicProfile, getJobs, getImageUrl, getUserReviews, createReview } from '../services/api';
import './Profile.css';

const PublicProfile = ({ language }) => {
    const { userId } = useParams();
    const [activeTab, setActiveTab] = useState('ads');
    const [user, setUser] = useState(null);
    const [userJobs, setUserJobs] = useState([]);
    const [reviews, setReviews] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingReviews, setLoadingReviews] = useState(false);
    const [error, setError] = useState(null);
    const [adTypeFilter, setAdTypeFilter] = useState('all');

    // Review Form State
    const [newReview, setNewReview] = useState({ rating: 0, text: '' });
    const [submittingReview, setSubmittingReview] = useState(false);
    const [submitSuccess, setSubmitSuccess] = useState(false);
    const [currentUser, setCurrentUser] = useState(null);

    useEffect(() => {
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
            setCurrentUser(JSON.parse(storedUser));
        }
    }, []);

    useEffect(() => {
        const loadUser = async () => {
            try {
                // Fetch User Profile
                const userRes = await getUserPublicProfile(userId);
                setUser(userRes.data);
            } catch (err) {
                console.error('Error loading user profile:', err);
                setError('Пользователь не найден или возникла ошибка загрузки.');
            }
        };

        if (userId) {
            loadUser();
            loadReviews();
        }
    }, [userId]);

    const loadReviews = async () => {
        if (!userId) return;
        try {
            setLoadingReviews(true);
            const res = await getUserReviews(userId);
            setReviews(res.data || []);
        } catch (err) {
            console.error('Error loading reviews:', err);
        } finally {
            setLoadingReviews(false);
        }
    };

    useEffect(() => {
        const loadJobs = async () => {
            if (!userId) return;
            try {
                setLoadingJobs(true);
                // Fetch User's Jobs with filter
                const options = { author: userId, limit: 'all' };
                if (adTypeFilter !== 'all') {
                    // Map filter to backend type option if needed, 
                    // assuming backend accepts 'SEEKING'/'OFFERING' via 'type' param
                    // but wait, server.js getJobs logic for type:
                    // it checks if (options.type) -> params.append('type', options.type);
                    // and backend checks: params.push(req.query.type.toUpperCase());
                    // so we pass 'seeking' or 'offering', backend uppercases it.
                    options.type = adTypeFilter;
                }
                const jobsRes = await getJobs(language, options);
                setUserJobs(jobsRes.data || []);
            } catch (err) {
                console.error('Error loading user jobs:', err);
            } finally {
                setLoadingJobs(false);
            }
        };

        loadJobs();
    }, [userId, language, adTypeFilter]);

    // Split loading state to allow profile to show while jobs load
    const [loadingJobs, setLoadingJobs] = useState(false);

    const handleReviewSubmit = async (e) => {
        e.preventDefault();
        if (!newReview.rating) {
            alert('Пожалуйста, выберите оценку');
            return;
        }
        if (!userId) return;

        setSubmittingReview(true);
        setSubmitSuccess(false);
        try {
            await createReview({
                targetUserId: userId,
                rating: newReview.rating,
                text: newReview.text
            });
            setNewReview({ rating: 0, text: '' });
            loadReviews(); // Reload list
            setSubmitSuccess(true);
            // Hide success message after 5 seconds
            setTimeout(() => setSubmitSuccess(false), 5000);
        } catch (err) {
            console.error('Error submitting review:', err);
            alert(err.response?.data?.error || 'Не удалось отправить отзыв');
        } finally {
            setSubmittingReview(false);
        }
    };

    const StarRating = ({ rating, interactive = false, onRatingChange }) => {
        return (
            <div className="star-rating" style={{ display: 'flex', gap: '5px' }}>
                {[1, 2, 3, 4, 5].map((star) => (
                    <span
                        key={star}
                        onClick={() => interactive && onRatingChange(star)}
                        style={{
                            cursor: interactive ? 'pointer' : 'default',
                            fontSize: interactive ? '24px' : '18px',
                            color: star <= rating ? '#9b59b6' : '#ccc' // Purple for active, gray for inactive
                        }}
                    >
                        {star <= rating ? '★' : '☆'}
                    </span>
                ))}
            </div>
        );
    };

    const cleanText = (text) => {
        if (!text) return '';
        return String(text)
            .replace(/&lt;/g, '<')
            .replace(/&gt;/g, '>')
            .replace(/&quot;/g, '"')
            .replace(/&#39;/g, "'")
            .replace(/&amp;/g, '&');
    };

    if (!user && loading) return <div className="loading">Loading...</div>;
    if (error || !user) return <div className="error-message">{error || 'User not found'}</div>;

    return (
        <div className="profile-page public">
            <div className="profile-header">
                <div className="profile-banner" style={{ backgroundImage: (user.banner || user.BANNER) ? `url(${getImageUrl(user.banner || user.BANNER)})` : 'none' }}></div>
                <div className="container">
                    <div className="profile-info-card">
                        <div className="profile-avatar-wrapper">
                            <div className="profile-avatar">
                                {user.avatar || user.AVATAR ? (
                                    <img src={getImageUrl(user.avatar || user.AVATAR)} alt="avatar" />
                                ) : (
                                    (user.name || user.NAME) ? (user.name || user.NAME).charAt(0).toUpperCase() : (user.login || user.LOGIN || '?').charAt(0).toUpperCase()
                                )}
                            </div>
                        </div>

                        <div className="profile-details">
                            <div className="name-rating-wrapper">
                                <h1 style={{ margin: 0 }}>{user.name || user.NAME || user.login || user.LOGIN}</h1>
                                {user.total_reviews > 0 && (
                                    <div className="profile-avg-rating">
                                        <StarRating rating={Math.round(user.avg_rating)} />
                                        <span className="rating-number">
                                            {Number(user.avg_rating).toFixed(1)} ({user.total_reviews})
                                        </span>
                                    </div>
                                )}
                            </div>
                            <p className="profile-meta">
                                Пользователь с {new Date(user.date_register || user.DATE_REGISTER || Date.now()).toLocaleDateString()}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            <div className="profile-content">
                <div className="container">
                    <div className="profile-tabs">
                        <button
                            className={`profile-tab ${activeTab === 'ads' ? 'active' : ''}`}
                            onClick={() => setActiveTab('ads')}
                        >
                            Объявления
                        </button>
                        <button
                            className={`profile-tab ${activeTab === 'reviews' ? 'active' : ''}`}
                            onClick={() => setActiveTab('reviews')}
                        >
                            Отзывы
                        </button>
                    </div>

                    <div className="tab-panel">
                        {activeTab === 'ads' && (
                            <div className="ads-tab">
                                <div className="tab-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '15px' }}>
                                    <h3>Объявления пользователя</h3>

                                    <div className="ad-filters" style={{ display: 'flex', gap: '10px' }}>
                                        <button
                                            className={`filter-pill ${adTypeFilter === 'all' ? 'active' : ''}`}
                                            onClick={() => setAdTypeFilter('all')}
                                            style={{
                                                padding: '6px 14px',
                                                borderRadius: '20px',
                                                border: '1px solid #ddd',
                                                background: adTypeFilter === 'all' ? '#8e44ad' : '#fff',
                                                color: adTypeFilter === 'all' ? '#fff' : '#666',
                                                cursor: 'pointer',
                                                fontSize: '14px',
                                                transition: 'all 0.2s'
                                            }}
                                        >
                                            Все
                                        </button>
                                        <button
                                            className={`filter-pill ${adTypeFilter === 'seeking' ? 'active' : ''}`}
                                            onClick={() => setAdTypeFilter('seeking')}
                                            style={{
                                                padding: '6px 14px',
                                                borderRadius: '20px',
                                                border: '1px solid #ddd',
                                                background: adTypeFilter === 'seeking' ? '#8e44ad' : '#fff',
                                                color: adTypeFilter === 'seeking' ? '#fff' : '#666',
                                                cursor: 'pointer',
                                                fontSize: '14px',
                                                transition: 'all 0.2s'
                                            }}
                                        >
                                            Ищу
                                        </button>
                                        <button
                                            className={`filter-pill ${adTypeFilter === 'offering' ? 'active' : ''}`}
                                            onClick={() => setAdTypeFilter('offering')}
                                            style={{
                                                padding: '6px 14px',
                                                borderRadius: '20px',
                                                border: '1px solid #ddd',
                                                background: adTypeFilter === 'offering' ? '#8e44ad' : '#fff',
                                                color: adTypeFilter === 'offering' ? '#fff' : '#666',
                                                cursor: 'pointer',
                                                fontSize: '14px',
                                                transition: 'all 0.2s'
                                            }}
                                        >
                                            Предлагаю
                                        </button>
                                    </div>
                                </div>

                                {loadingJobs ? (
                                    <div className="loading" style={{ padding: '40px', textAlign: 'center', color: '#888' }}>Загрузка объявлений...</div>
                                ) : userJobs.length > 0 ? (
                                    <div className="jobs-list">
                                        {userJobs.map(job => (
                                            <div key={job.ID} className="profile-job-card">
                                                {job.PREVIEW_PICTURE_SRC && (
                                                    <div className="job-image">
                                                        <img src={getImageUrl(job.PREVIEW_PICTURE_SRC)} alt={job.NAME} />
                                                    </div>
                                                )}
                                                <div className="job-info">
                                                    <h4>{job.NAME}</h4>
                                                    <p className="job-desc" dangerouslySetInnerHTML={{ __html: cleanText(job.PREVIEW_TEXT) }} />
                                                    {job.PROPERTIES?.PRICE?.VALUE && <p className="job-price">{job.PROPERTIES.PRICE.VALUE}</p>}
                                                    <Link to={`/work/${job.CODE}`} className="btn-view">Просмотреть</Link>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="empty-state">
                                        <p>У пользователя пока нет активных объявлений</p>
                                    </div>
                                )}
                            </div>
                        )}
                        {activeTab === 'reviews' && (
                            <div className="reviews-tab">
                                <div className="tab-header" style={{ marginBottom: '20px' }}>
                                    <h3>Отзывы ({reviews.length})</h3>
                                </div>

                                {/* Review List */}
                                {loadingReviews ? (
                                    <div className="loading" style={{ padding: '20px', textAlign: 'center' }}>Загрузка отзывов...</div>
                                ) : reviews.length > 0 ? (
                                    <div className="reviews-list" style={{ display: 'flex', flexDirection: 'column', gap: '20px', marginBottom: '40px' }}>
                                        {reviews.map(review => (
                                            <div key={review.id} className="review-card" style={{ background: '#f9f9f9', padding: '15px', borderRadius: '10px', color: '#111' }}>
                                                <div className="review-header" style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
                                                    <div className="reviewer-info" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                                        <div className="reviewer-avatar" style={{ width: '32px', height: '32px', borderRadius: '50%', background: '#eee', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', fontWeight: 'bold' }}>
                                                            {review.author_avatar_src ? (
                                                                <img src={getImageUrl(review.author_avatar_src)} alt="avatar" style={{ width: '100%', height: '100%', object_fit: 'cover' }} />
                                                            ) : (
                                                                (review.author_name || review.author_login || '?').charAt(0).toUpperCase()
                                                            )}
                                                        </div>
                                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                                            <Link to={`/profile/${review.author_id}`} className="reviewer-name" style={{ fontWeight: 'bold', color: '#8e44ad', textDecoration: 'none' }}>
                                                                {review.author_name || review.author_login}
                                                            </Link>
                                                            <StarRating rating={review.rating} />
                                                        </div>
                                                    </div>
                                                    <span className="review-date" style={{ fontSize: '12px', color: '#888' }}>
                                                        {new Date(review.date_create).toLocaleDateString()}
                                                    </span>
                                                </div>
                                                <p className="review-text" style={{ margin: 0, lineHeight: '1.4' }}>{review.text}</p>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="empty-state" style={{ marginBottom: '40px' }}>
                                        <p>У пользователя пока нет отзывов</p>
                                    </div>
                                )}

                                {/* Write Review Form */}
                                {currentUser ? (
                                    userId !== String(currentUser.id) ? (
                                        <div className="write-review-form" style={{ background: '#fff', padding: '20px', borderRadius: '10px', boxShadow: '0 2px 10px rgba(0,0,0,0.1)' }}>
                                            <h4 style={{ marginBottom: '15px', color: '#333' }}>Оставить отзыв</h4>

                                            {submitSuccess && (
                                                <div className="success-message" style={{ background: '#d4edda', color: '#155724', padding: '12px', borderRadius: '5px', marginBottom: '20px', textAlign: 'center', fontWeight: 'bold' }}>
                                                    Отзыв успешно добавлен!
                                                </div>
                                            )}
                                            <form onSubmit={handleReviewSubmit}>
                                                <div className="form-group" style={{ marginBottom: '15px' }}>
                                                    <label style={{ display: 'block', marginBottom: '5px', color: '#666' }}>Ваша оценка:</label>
                                                    <StarRating
                                                        rating={newReview.rating}
                                                        interactive={true}
                                                        onRatingChange={(val) => setNewReview({ ...newReview, rating: val })}
                                                    />
                                                </div>
                                                <div className="form-group" style={{ marginBottom: '15px' }}>
                                                    <label style={{ display: 'block', marginBottom: '5px', color: '#666' }}>Ваш комментарий:</label>
                                                    <textarea
                                                        value={newReview.text}
                                                        onChange={(e) => setNewReview({ ...newReview, text: e.target.value })}
                                                        placeholder="Напишите ваш отзыв..."
                                                        style={{ width: '100%', padding: '10px', borderRadius: '5px', border: '1px solid #ddd', minHeight: '100px' }}
                                                        required
                                                    />
                                                </div>
                                                <button
                                                    type="submit"
                                                    disabled={submittingReview}
                                                    style={{
                                                        background: '#8e44ad',
                                                        color: '#fff',
                                                        border: 'none',
                                                        padding: '10px 20px',
                                                        borderRadius: '5px',
                                                        cursor: 'pointer',
                                                        opacity: submittingReview ? 0.7 : 1
                                                    }}
                                                >
                                                    {submittingReview ? 'Отправка...' : 'Отправить отзыв'}
                                                </button>
                                            </form>
                                        </div>
                                    ) : (
                                        <p style={{ color: '#888', fontStyle: 'italic' }}>Вы не можете оставить отзыв самому себе.</p>
                                    )
                                ) : (
                                    <div className="login-to-review" style={{ background: '#f0f0f0', padding: '15px', borderRadius: '5px', textAlign: 'center' }}>
                                        <p style={{ margin: 0, color: '#666' }}>
                                            Пожалуйста, <Link to="/auth" style={{ color: '#8e44ad', fontWeight: 'bold' }}>войдите в систему</Link>, чтобы оставить отзыв.
                                        </p>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PublicProfile;
